import { useState, type ButtonHTMLAttributes } from "react";
import { animated, useSpring } from "react-spring";
import { twMerge } from "tailwind-merge";

interface IBaseButton {
  colorBase?: "blue" | "yellow" | "gray";
  minWidth?: string;
  isSquare?: boolean;
  disabledStyle?: string;
}

type TBaseButtonProps = IBaseButton & ButtonHTMLAttributes<HTMLButtonElement>;

const BaseButton: React.FC<TBaseButtonProps> = (props) => {
  const {
    colorBase = "blue",
    minWidth,
    isSquare,
    disabledStyle,
    children,
    disabled,
    className,
    ...otherProps
  } = props;

  const [hovered, setHovered] = useState(false);

  const animation = useSpring({
    backgroundSize: hovered ? "100% 100%" : "0% 100%",
    color: hovered ? "#000" : "#FFF",
    config: { tension: 400, friction: 26, clamp: true },
  });

  const COLOR_MAP = [
    {
      base: "blue",
      backgroundClass: "bg-mainLightBlue",
      gradient: "linear-gradient(to right, #0491BE, #00f2fe)",
    },
    {
      base: "yellow",
      backgroundClass: "bg-mainYellow",
      gradient: "linear-gradient(to right, #FFC200, #f0df1f)",
    },
    {
      base: "gray",
      backgroundClass: "bg-mainDisable",
      gradient: "linear-gradient(to right, #D9D9D9, #EAEAEA)",
    },
  ] as const;

  return (
    <animated.button
      className={twMerge(
        "flex h-14 min-h-min text-sm font-semibold gap-2 items-center justify-center py-[4px] transition-all duration-50 ease-in-out bg-no-repeat",
        minWidth ?? "min-w-36",
        isSquare ? "rounded-xs" : "rounded-3xl",
        COLOR_MAP.find((color) => color.base === colorBase)?.backgroundClass ??
          "bg-mainLightBlue",
        disabled
          ? twMerge(
              "disabled:text-gray-400 disabled:bg-gray-200",
              disabledStyle
            )
          : "",
        className
      )}
      style={{
        ...animation,
        backgroundImage:
          COLOR_MAP.find((color) => color.base === colorBase)?.gradient ?? "",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      disabled={disabled}
      {...otherProps}
    >
      {children}
    </animated.button>
  );
};

export default BaseButton;
